/**
 * signupStore – mirrors the mobile app's DataStorage for the multi-step signup flow.
 *
 * Data is kept in sessionStorage so it survives soft navigations between steps
 * but is cleared on tab/browser close (appropriate for a signup draft).
 *
 * A module-level variable holds the actual profile image File so it never has
 * to be serialised (same as Flutter's in-memory DataStorage approach).
 */

import type { LocationBody } from '../services/locationService';

export type { LocationBody };

export interface SignupDraft {
  /** True while the user is actively going through the signup funnel. */
  flowActive: boolean;
  email?: string;
  /** 'fan' | 'creator' – matches mobile ACCOUNT_TYPE storage */
  role?: 'fan' | 'creator';
  fullName?: string;
  firstName?: string;
  username?: string;
  /** ISO date string (YYYY-MM-DD) – matches the API expectation */
  dateOfBirth?: string;
  password?: string;
  profileImageUrl?: string;
  genres?: string[];
  /** Genre names typed by the user during signup that don't exist yet in the API.
   *  These will be created via the API after successful account creation. */
  customGenres?: string[];
  /** Selected location – stored after SearchLocationPage step. */
  locationBody?: LocationBody;
  /** Optional profile picture URL from social login (e.g. Google/Apple). */
  profilePicUrl?: string;
  /** True if the user is signing up via Google. */
  googleSignedUp?: boolean;
  /** True if the user is signing up via Apple. */
  appleSignedUp?: boolean;
  /** Apple user identifier (`sub`) returned from Sign In with Apple. */
  appleUserId?: string;
}

// Module-level – not serialisable, so kept in JS memory (mirrors Flutter DataStorage)
let _profileImageFile: File | null = null;

const SESSION_KEY = 'musicade_signup_draft';

export const signupStore = {
  /** Read the current draft from sessionStorage. */
  get(): SignupDraft {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      return { flowActive: false, ...JSON.parse(raw ?? '{}') };
    } catch {
      return { flowActive: false };
    }
  },

  /** Merge partial data into the stored draft. */
  set(data: Partial<SignupDraft>): void {
    const current = this.get();
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ ...current, ...data }));
  },

  /** Returns true when the user is inside the email→verify→account creation funnel. */
  isActive(): boolean {
    return this.get().flowActive === true;
  },

  /** Clear all signup state (call after successful signup or user cancels). */
  clear(): void {
    sessionStorage.removeItem(SESSION_KEY);
    _profileImageFile = null;
  },

  /** Store the chosen profile image file in memory. */
  setProfileImage(file: File | null): void {
    _profileImageFile = file;
  },

  /** Retrieve the stored profile image file. */
  getProfileImage(): File | null {
    return _profileImageFile;
  },
};
